import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Progress } from "../ui/progress";
import { Badge } from "../ui/badge";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { ShieldCheck, ShieldAlert, ShieldQuestion, FolderCheck, ListTodo, AlertTriangle, Building2, Megaphone } from "lucide-react";
import { cn } from "../../lib/utils";

function Kpi({ icon: Icon, label, value, tone, testId }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</span>
        <Icon className={cn("w-4 h-4", tone)} />
      </div>
      <div className="text-[26px] font-bold leading-none">{value}</div>
    </div>
  );
}

export default function ComplianceDashboard({ onNavigate, onViewFrameworksGrid }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/compliance/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-20 rounded-xl bg-muted/40 animate-pulse" />)}</div>;
  }

  return (
    <div className="space-y-4" data-testid="compliance-dashboard">
      {data.unplanned_audit_frameworks?.length > 0 && (
        <div
          className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 flex items-center gap-2.5 text-[13px]"
          data-testid="compliance-audit-banner"
        >
          <Megaphone className="w-4 h-4 text-amber-600 shrink-0" />
          <span>
            <strong>{data.unplanned_audit_frameworks.join(", ")}</strong> {data.unplanned_audit_frameworks.length === 1 ? "is" : "are"} enabled but not yet on the audit calendar.{" "}
            <button type="button" className="underline font-medium" onClick={() => onNavigate?.("audits")} data-testid="compliance-audit-banner-link">Plan an audit</button>
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi icon={ShieldCheck} label="Readiness" value={`${data.readiness_pct}%`} tone="text-emerald-600" testId="kpi-readiness" />
        <Kpi icon={ShieldAlert} label="Failing controls" value={data.controls_failing} tone="text-red-500" testId="kpi-failing" />
        <Kpi icon={ShieldQuestion} label="No evidence" value={data.controls_no_evidence} tone="text-amber-500" testId="kpi-no-evidence" />
        <Kpi icon={FolderCheck} label="Evidence items" value={data.evidence_total} tone="text-primary" testId="kpi-evidence" />
        <Kpi icon={ListTodo} label="Open tasks" value={data.open_tasks} tone="text-sky-500" testId="kpi-tasks" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-4" data-testid="compliance-readiness-trend">
          <div className="flex items-center justify-between mb-1">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Readiness trend</div>
              <div className="text-[15px] font-semibold">Last 7 days</div>
            </div>
          </div>
          <div className="h-52 mt-2">
            <ResponsiveContainer>
              <AreaChart data={data.trend} margin={{ top: 4, right: 6, bottom: 0, left: -10 }}>
                <defs>
                  <linearGradient id="readinessFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} width={30} domain={[0, 100]} />
                <Tooltip contentStyle={{ fontSize: 11 }} formatter={(v) => [`${v}%`, "Readiness"]} />
                <Area type="monotone" dataKey="readiness" stroke="#10b981" fill="url(#readinessFill)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[10.5px] text-muted-foreground mt-1">Modelled from current control health totals.</div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="compliance-control-breakdown">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Control health</div>
          {[
            { label: "Healthy", value: data.controls_healthy, barClass: "[&>div]:bg-emerald-500" },
            { label: "Failing", value: data.controls_failing, barClass: "[&>div]:bg-red-500" },
            { label: "No evidence", value: data.controls_no_evidence, barClass: "[&>div]:bg-amber-500" },
          ].map((row) => (
            <div key={row.label}>
              <div className="flex items-center justify-between text-[12.5px] mb-1">
                <span className="text-muted-foreground">{row.label}</span>
                <span className="font-semibold">{row.value} / {data.controls_total}</span>
              </div>
              <Progress value={data.controls_total ? (row.value / data.controls_total) * 100 : 0} className={cn("h-1.5", row.barClass)} />
            </div>
          ))}
          <button
            type="button"
            onClick={() => onNavigate?.("controls")}
            className="text-[12px] font-medium text-primary hover:underline"
            data-testid="compliance-view-controls-link"
          >
            View all controls →
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4" data-testid="compliance-framework-breakdown">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Framework readiness</span>
          <button type="button" onClick={onViewFrameworksGrid} className="text-[11.5px] text-primary hover:underline" data-testid="compliance-view-frameworks-grid-link">
            View breakdown →
          </button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(data.framework_breakdown || []).map((fw) => (
            <button
              type="button"
              key={fw.key}
              onClick={onViewFrameworksGrid}
              className="text-left rounded-lg border border-border/70 p-3 hover:border-primary/40 transition-colors"
              data-testid={`compliance-framework-row-${fw.key}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[12.5px] font-semibold truncate">{fw.name}</span>
                <span className={cn(
                  "text-[12px] font-bold shrink-0 ml-2",
                  fw.maturity_pct >= 90 ? "text-emerald-600" : fw.maturity_pct >= 50 ? "text-amber-600" : "text-red-500",
                )}>{fw.maturity_pct}%</span>
              </div>
              {fw.key === "iso27001" && (
                <Badge
                  variant="outline"
                  data-testid="iso27001-annex-a-progress-badge"
                  className="mb-1.5 text-[10.5px] font-semibold border-primary/30 text-primary bg-primary/5"
                >
                  {fw.healthy}/{fw.total_controls} Annex A controls evidenced
                </Badge>
              )}
              <Progress
                value={fw.maturity_pct}
                className={cn("h-1.5", fw.maturity_pct >= 90 ? "[&>div]:bg-emerald-500" : fw.maturity_pct >= 50 ? "[&>div]:bg-amber-500" : "[&>div]:bg-red-500")}
              />
              <div className="flex items-center justify-between mt-1.5 text-[11px] text-muted-foreground">
                {fw.basis === "controls" && <span>{fw.healthy} healthy / {fw.failing} failing · {fw.total_controls} controls</span>}
                {fw.basis === "audit" && <span>Latest audit: {fw.audit_stage}</span>}
                {fw.basis === "none" && <span>No controls or audits mapped yet</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-card p-4" data-testid="compliance-top-risks">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <div className="text-[13px] font-semibold">Top risks</div>
          </div>
          <div className="space-y-2">
            {data.top_risks.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 text-[12.5px]" data-testid={`compliance-top-risk-${r.id}`}>
                <span className="truncate">{r.code} — {r.title}</span>
                <span className={cn(
                  "text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 border shrink-0",
                  r.inherent_level === "critical" && "border-red-500/40 bg-red-500/10 text-red-600",
                  r.inherent_level === "high" && "border-orange-500/40 bg-orange-500/10 text-orange-600",
                  r.inherent_level === "medium" && "border-amber-500/40 bg-amber-500/10 text-amber-600",
                  r.inherent_level === "low" && "border-emerald-500/40 bg-emerald-500/10 text-emerald-600",
                )}>{r.inherent_level}</span>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => onNavigate?.("risks")} className="text-[12px] font-medium text-primary hover:underline mt-3" data-testid="compliance-view-risks-link">View risk register →</button>
        </div>

        <div className="rounded-xl border border-border bg-card p-4" data-testid="compliance-top-vendors">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-4 h-4 text-primary" />
            <div className="text-[13px] font-semibold">Highest-criticality vendors</div>
          </div>
          <div className="space-y-2">
            {data.top_vendors.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-2 text-[12.5px]" data-testid={`compliance-top-vendor-${v.id}`}>
                <span className="truncate">{v.name}</span>
                <span className={cn(
                  "text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 border shrink-0",
                  v.criticality === "critical" && "border-red-500/40 bg-red-500/10 text-red-600",
                  v.criticality === "high" && "border-orange-500/40 bg-orange-500/10 text-orange-600",
                  v.criticality === "medium" && "border-amber-500/40 bg-amber-500/10 text-amber-600",
                )}>{v.criticality}</span>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => onNavigate?.("vendors")} className="text-[12px] font-medium text-primary hover:underline mt-3" data-testid="compliance-view-vendors-link">View vendors →</button>
        </div>
      </div>
    </div>
  );
}
